import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Loader2, CheckCircle } from 'lucide-react';
import { ApiTypes } from '@/types/server/api.types';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/select';

import { logError } from '@/utils/logger.utils';
import { handleLogout } from '@/utils/auth.utils';

interface PreInterviewFormProps {
	onNext: () => void;
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data'] | null
	) => void;
}

interface FormState {
	coreTechnologies: string[];
	nonCoreTechnologies: string[];
	countryCode: string;
	phoneNumber: string;
	location: string;
	customLocation: string;
	hasManagedTeams: boolean;
	isCurrentlyEmployed: boolean;
	monthsSinceLastFullTimeRole: string | null;
	noticePeriodInDays: string | null;
	yearsOfFullTimeTechnicalExperience: string | null;
	yearsOfFullTimeStartupWorkExperience: string | null;
	pastOrganizationsKinds: string[];
	currentCTC: string | null;
	expectedCTC: string | null;
}

export const PreInterviewForm = ({
	onNext,
	setIsUserLoggedIn,
	setUserDetails,
}: PreInterviewFormProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionStatus, setSubmissionStatus] = useState<
		'submitted' | 'pending' | null
	>(null);
	const [questionOptions, setQuestionOptions] = useState<
		| ApiTypes.TGetCandidateSelfSubmittedProfileQuestionOptionsResponse['data']
		| null
	>(null);
	const [isLoading, setIsLoading] = useState(true);

	const [formState, setFormState] = useState<FormState>({
		coreTechnologies: [],
		nonCoreTechnologies: [],
		countryCode: '91',
		phoneNumber: '',
		location: '',
		customLocation: '',
		hasManagedTeams: false,
		isCurrentlyEmployed: false,
		monthsSinceLastFullTimeRole: null,
		noticePeriodInDays: null,
		yearsOfFullTimeTechnicalExperience: null,
		yearsOfFullTimeStartupWorkExperience: null,
		pastOrganizationsKinds: [],
		currentCTC: null,
		expectedCTC: null,
	});

	const handleCoreTechnologyChange = (technologies: string[]) => {
		setFormState((prev) => {
			// Remove from non-core if any of the new core technologies were there
			const newNonCoreTechnologies = prev.nonCoreTechnologies.filter(
				(t) => !technologies.includes(t)
			);

			return {
				...prev,
				coreTechnologies: technologies,
				nonCoreTechnologies: newNonCoreTechnologies,
			};
		});
	};

	const handleNonCoreTechnologyChange = (technologies: string[]) => {
		setFormState((prev) => {
			// Check if any of the newly selected technologies are already in core technologies
			const newlySelectedTechnologies = technologies.filter(
				(tech) => !prev.nonCoreTechnologies.includes(tech)
			);

			const conflictingTechnologies = newlySelectedTechnologies.filter((tech) =>
				prev.coreTechnologies.includes(tech)
			);

			// Show toast for conflicting technologies
			if (conflictingTechnologies.length > 0) {
				toast('Technology Already Selected', {
					description: `${conflictingTechnologies.join(', ')} ${
						conflictingTechnologies.length === 1 ? 'is' : 'are'
					} already selected as core technolog${
						conflictingTechnologies.length === 1 ? 'y' : 'ies'
					}.`,
					id: 'core-technology-conflict',
				});
			}

			// Remove any core technologies from non-core technologies (backend does this)
			const filteredTechnologies = technologies.filter(
				(tech) => !prev.coreTechnologies.includes(tech)
			);

			return {
				...prev,
				nonCoreTechnologies: filteredTechnologies,
			};
		});
	};

	const handlePastOrganizationKindChange = (kinds: string[]) => {
		setFormState((prev) => {
			return {
				...prev,
				pastOrganizationsKinds: kinds,
			};
		});
	};

	const handleLocationChange = (location: string) => {
		setFormState((prev) => ({
			...prev,
			location,
			customLocation: location === 'Others' ? prev.customLocation : '',
		}));
	};

	// Integer-only input handler (allows 0)
	const handleIntegerInput = (value: string, field: keyof FormState) => {
		// Store the raw input value
		setFormState((prev) => ({
			...prev,
			[field]: value === '' ? null : value,
		}));
	};

	// Number input handler (allows decimals and 0)
	const handleNumberInput = (value: string, field: keyof FormState) => {
		// Store the raw input value
		setFormState((prev) => ({
			...prev,
			[field]: value === '' ? null : value,
		}));
	};

	// Phone number input handler (only numbers)
	const handlePhoneNumberInput = (value: string) => {
		// Only allow numbers
		const numericValue = value.replace(/[^0-9]/g, '');

		setFormState((prev) => ({
			...prev,
			phoneNumber: numericValue,
		}));
	};

	// Country code input handler (only numbers and +)
	const handleCountryCodeInput = (value: string) => {
		// Only allow numbers and + at the beginning
		let cleanedValue = value.replace(/[^0-9+]/g, '');

		// Ensure + is only at the beginning
		if (cleanedValue.includes('+')) {
			const parts = cleanedValue.split('+');
			cleanedValue = '+' + parts.join('').replace(/[^0-9]/g, '');
		}

		setFormState((prev) => ({
			...prev,
			countryCode: cleanedValue,
		}));
	};

	const isFormValid = () => {
		const {
			coreTechnologies,
			nonCoreTechnologies,
			countryCode,
			phoneNumber,
			location,
			customLocation,
			hasManagedTeams,
			isCurrentlyEmployed,
			monthsSinceLastFullTimeRole,
			noticePeriodInDays,
			yearsOfFullTimeTechnicalExperience,
			yearsOfFullTimeStartupWorkExperience,
			pastOrganizationsKinds,
			currentCTC,
			expectedCTC,
		} = formState;

		// Core technologies validation (3-5 required, unique)
		const uniqueCoreTechnologies = Array.from(new Set(coreTechnologies));
		if (
			uniqueCoreTechnologies.length < 3 ||
			uniqueCoreTechnologies.length > 5
		) {
			return false;
		}

		// Non-core technologies validation (at least 1 required, unique, no overlap with core)
		const uniqueNonCoreTechnologies = Array.from(new Set(nonCoreTechnologies));
		const filteredNonCoreTechnologies = uniqueNonCoreTechnologies.filter(
			(tech) => !uniqueCoreTechnologies.includes(tech)
		);
		if (filteredNonCoreTechnologies.length === 0) {
			return false;
		}

		// Country code validation (required and must be numeric)
		if (!countryCode || !countryCode.trim()) {
			return false;
		}
		const slicedCountryCode = countryCode.startsWith('+')
			? countryCode.slice(1).trim()
			: countryCode.trim();
		if (!slicedCountryCode || !/^\d+$/.test(slicedCountryCode)) {
			return false;
		}

		// Phone number validation (required and must be numeric)
		if (!phoneNumber || !phoneNumber.trim()) {
			return false;
		}
		if (!/^\d+$/.test(phoneNumber)) {
			return false;
		}

		// Location validation
		if (!location || !location.trim()) {
			return false;
		}
		if (location === 'Others' && !customLocation.trim()) {
			return false;
		}

		// Boolean fields validation
		if (
			typeof hasManagedTeams !== 'boolean' ||
			typeof isCurrentlyEmployed !== 'boolean'
		) {
			return false;
		}

		// Employment-specific validation
		if (!isCurrentlyEmployed) {
			if (
				!monthsSinceLastFullTimeRole ||
				monthsSinceLastFullTimeRole.trim() === '' ||
				isNaN(parseInt(monthsSinceLastFullTimeRole, 10)) ||
				parseInt(monthsSinceLastFullTimeRole, 10) < 0
			) {
				return false;
			}
		} else {
			if (
				!noticePeriodInDays ||
				noticePeriodInDays.trim() === '' ||
				isNaN(parseInt(noticePeriodInDays, 10)) ||
				parseInt(noticePeriodInDays, 10) < 0
			) {
				return false;
			}
		}

		// Experience validation (must be numbers, not NaN, allows 0 and decimals)
		if (
			!yearsOfFullTimeTechnicalExperience ||
			yearsOfFullTimeTechnicalExperience.trim() === '' ||
			isNaN(parseFloat(yearsOfFullTimeTechnicalExperience)) ||
			parseFloat(yearsOfFullTimeTechnicalExperience) < 0
		) {
			return false;
		}

		if (
			!yearsOfFullTimeStartupWorkExperience ||
			yearsOfFullTimeStartupWorkExperience.trim() === '' ||
			isNaN(parseFloat(yearsOfFullTimeStartupWorkExperience)) ||
			parseFloat(yearsOfFullTimeStartupWorkExperience) < 0
		) {
			return false;
		}

		// Past organizations validation (at least 1 required, unique)
		const uniquePastOrganizationsKinds = Array.from(
			new Set(pastOrganizationsKinds)
		);
		if (
			!uniquePastOrganizationsKinds ||
			uniquePastOrganizationsKinds.length === 0
		) {
			return false;
		}

		// CTC validation (both required, must be numbers, and positive)
		if (
			!currentCTC ||
			currentCTC.trim() === '' ||
			isNaN(parseFloat(currentCTC)) ||
			parseFloat(currentCTC) <= 0
		) {
			return false;
		}

		if (
			!expectedCTC ||
			expectedCTC.trim() === '' ||
			isNaN(parseFloat(expectedCTC)) ||
			parseFloat(expectedCTC) <= 0
		) {
			return false;
		}

		return true;
	};

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			const token = window.localStorage.getItem('token');

			// Prepare the request body based on employment status
			const requestBody: {
				selfSubmittedProfile: {
					coreTechnologies: string[];
					nonCoreTechnologies: string[];
					countryCode: string;
					phoneNumber: string;
					location: string;
					hasManagedTeams: boolean;
					isCurrentlyEmployed: boolean;
					yearsOfFullTimeTechnicalExperience: number;
					yearsOfFullTimeStartupWorkExperience: number;
					pastOrganizationsKinds: string[];
					currentCTCInLPA: number;
					expectedCTCInLPA: number;
					noticePeriodInDays?: number;
					monthsSinceLastFullTimeRole?: number;
				};
			} = {
				selfSubmittedProfile: {
					coreTechnologies: Array.from(new Set(formState.coreTechnologies)),
					nonCoreTechnologies: Array.from(
						new Set(formState.nonCoreTechnologies)
					),
					countryCode: formState.countryCode.startsWith('+')
						? formState.countryCode.slice(1).trim()
						: formState.countryCode.trim(),
					phoneNumber: formState.phoneNumber.trim(),
					location:
						formState.location === 'Others'
							? formState.customLocation.trim()
							: formState.location.trim(),
					hasManagedTeams: formState.hasManagedTeams,
					isCurrentlyEmployed: formState.isCurrentlyEmployed,
					yearsOfFullTimeTechnicalExperience: parseFloat(
						formState.yearsOfFullTimeTechnicalExperience!
					),
					yearsOfFullTimeStartupWorkExperience: parseFloat(
						formState.yearsOfFullTimeStartupWorkExperience!
					),
					pastOrganizationsKinds: Array.from(
						new Set(formState.pastOrganizationsKinds)
					),
					currentCTCInLPA: parseFloat(formState.currentCTC!),
					expectedCTCInLPA: parseFloat(formState.expectedCTC!),
				},
			};

			// Add employment-specific fields
			if (formState.isCurrentlyEmployed) {
				requestBody.selfSubmittedProfile.noticePeriodInDays = parseInt(
					formState.noticePeriodInDays!,
					10
				);
			} else {
				requestBody.selfSubmittedProfile.monthsSinceLastFullTimeRole = parseInt(
					formState.monthsSinceLastFullTimeRole!,
					10
				);
			}

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/candidates/profile`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify(requestBody),
				}
			);

			const data =
				(await response.json()) as ApiTypes.TUpdateCandidateSelfSubmittedProfileResponse;

			if (data.httpResponseCode !== 200) {
				toast('Form Submission Error', {
					description: data.message,
					id: 'form-submission-error',
				});

				if (data.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				return;
			}

			setSubmissionStatus('submitted');
			onNext();
			return;
		} catch (error) {
			logError(error);

			toast('Form Submission Error', {
				description:
					'Failed to submit form. Please refresh the page and try again.',
				id: 'form-submission-error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const getPreInterviewFormSubmissionStatus = async () => {
		try {
			const token = window.localStorage.getItem('token');

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/candidates/profile/status`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const data =
				(await response.json()) as ApiTypes.TGetCandidateSelfSubmittedProfileStatusResponse;

			if (data.httpResponseCode !== 200) {
				toast('Error getting form status', {
					description: data.message,
					id: 'pre-interview-form-submission-status-error',
				});

				if (data.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				return;
			}

			if (!data.data) {
				throw new Error('No data returned from form status request');
			}

			if (data.data.isProfileComplete === true) {
				onNext();
				return;
			}

			const questionOptionsResponse = await fetch(
				`${
					import.meta.env.VITE_SERVER_API_PREFIX
				}/candidates/profile/question-options`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const questionOptionsData =
				(await questionOptionsResponse.json()) as ApiTypes.TGetCandidateSelfSubmittedProfileQuestionOptionsResponse;

			if (questionOptionsData.httpResponseCode !== 200) {
				toast('Error getting question options', {
					description: questionOptionsData.message,
					id: 'pre-interview-form-question-options-error',
				});

				if (questionOptionsData.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				return;
			}

			setQuestionOptions(questionOptionsData.data);

			setSubmissionStatus('pending');
			setIsLoading(false);
		} catch (error) {
			logError(error);

			toast('Form Status Error', {
				description:
					'Failed to get form status. Please refresh the page and try again.',
				id: 'form-status-error',
			});
		}
	};

	useEffect(() => {
		getPreInterviewFormSubmissionStatus();
	}, []);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64 bg-white rounded-md shadow-md">
				<Loader2 className="h-10 w-10 animate-spin text-blue-600" />
			</div>
		);
	}

	// Show completed state if the form has already been submitted
	if (submissionStatus === 'submitted') {
		return (
			<div className="bg-white rounded-md shadow-lg p-6 space-y-6 border border-gray-100 transition-all">
				<div>
					<h2 className="text-xl font-semibold text-gray-800">
						Complete your profile
					</h2>
					<p className="text-sm text-gray-500 mt-3">
						Please help us in getting to know you better by completing the
						following form.
					</p>
				</div>

				<Separator />

				<div className="rounded-xl p-6 flex flex-col items-center justify-center text-center space-y-4 bg-green-50 border border-green-100">
					<CheckCircle className="h-10 w-10 text-green-600" />
					<div>
						<p className="text-lg font-medium text-green-700 mb-1">
							Profile Completed
						</p>
						<p className="text-sm text-green-600">
							Your profile information will be used to match you with suitable
							positions.
						</p>
					</div>
				</div>

				<div className="flex justify-end pt-4">
					<Button
						onClick={onNext}
						className="bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
					>
						Next
					</Button>
				</div>
			</div>
		);
	}

	if (!questionOptions) {
		return (
			<div className="flex justify-center items-center h-64 bg-white rounded-md shadow-md">
				<Loader2 className="h-10 w-10 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<div className="bg-white rounded-md shadow-lg p-8 space-y-8 border border-gray-100 transition-all">
			<div>
				<h2 className="text-2xl font-semibold text-gray-800 mb-3">
					Complete your profile
				</h2>
				<p className="text-sm text-gray-500">
					Please help us in getting to know you better by completing the
					following form.
				</p>
			</div>

			<Separator />

			<div className="space-y-10">
				{/* Core Technologies */}
				<div className="space-y-4">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							Which technologies are you most proficient in? (Select 3-5)
						</Label>
						<p className="text-sm text-gray-500">
							Your interview will revolve around only the technologies that you
							are most proficient in.
						</p>
					</div>
					<MultiSelect
						options={questionOptions.coreTechnologies}
						selectedValues={formState.coreTechnologies}
						onSelectionChange={handleCoreTechnologyChange}
						placeholder="Select core technologies..."
						maxSelections={5}
					/>
				</div>

				{/* Non-Core Technologies */}
				<div className="space-y-4">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							Other technologies that you know (Select at least 1)
						</Label>
						<p className="text-sm text-gray-500">
							Please select all the technologies that you know apart from core
							technologies.
						</p>
					</div>
					<MultiSelect
						options={questionOptions.nonCoreTechnologies}
						selectedValues={formState.nonCoreTechnologies}
						onSelectionChange={handleNonCoreTechnologyChange}
						placeholder="Select other technologies..."
					/>
				</div>

				{/* Contact Information */}
				<div className="space-y-6">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							Contact Information
						</Label>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<div className="space-y-2">
							<Label
								htmlFor="countryCode"
								className="text-sm font-medium text-gray-600"
							>
								Country Code
							</Label>
							<Input
								id="countryCode"
								type="text"
								placeholder="91"
								value={formState.countryCode}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleCountryCodeInput(e.target.value)
								}
							/>
						</div>
						<div className="space-y-2">
							<Label
								htmlFor="phoneNumber"
								className="text-sm font-medium text-gray-600"
							>
								Phone Number
							</Label>
							<Input
								id="phoneNumber"
								type="tel"
								placeholder="9691070020"
								value={formState.phoneNumber}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handlePhoneNumberInput(e.target.value)
								}
							/>
						</div>
						<div className="space-y-2">
							<Label
								htmlFor="location"
								className="text-sm font-medium text-gray-600"
							>
								Current Location
							</Label>
							<select
								id="location"
								value={formState.location}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
									handleLocationChange(e.target.value)
								}
								className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">Select location...</option>
								{questionOptions.allowedLocations.map((location) => (
									<option key={location} value={location}>
										{location}
									</option>
								))}
								<option value="Others">Others</option>
							</select>
							{formState.location === 'Others' && (
								<Input
									type="text"
									placeholder="City, State"
									value={formState.customLocation}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setFormState((prev) => ({
											...prev,
											customLocation: e.target.value,
										}))
									}
								/>
							)}
						</div>
					</div>
				</div>

				{/* Experience Questions */}
				<div className="space-y-6">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							Experience & Background
						</Label>
					</div>

					<div className="space-y-6">
						<div className="space-y-4">
							<Label className="text-sm font-medium text-gray-700">
								Do you have experience leading or managing development teams?
							</Label>
							<div className="flex items-center space-x-8">
								<div className="flex items-center space-x-3">
									<input
										type="radio"
										id="hasManagedTeamsYes"
										name="hasManagedTeams"
										checked={formState.hasManagedTeams === true}
										onChange={() =>
											setFormState((prev) => ({
												...prev,
												hasManagedTeams: true,
											}))
										}
										className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<Label
										htmlFor="hasManagedTeamsYes"
										className="text-sm text-gray-700"
									>
										Yes
									</Label>
								</div>
								<div className="flex items-center space-x-3">
									<input
										type="radio"
										id="hasManagedTeamsNo"
										name="hasManagedTeams"
										checked={formState.hasManagedTeams === false}
										onChange={() =>
											setFormState((prev) => ({
												...prev,
												hasManagedTeams: false,
											}))
										}
										className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<Label
										htmlFor="hasManagedTeamsNo"
										className="text-sm text-gray-700"
									>
										No
									</Label>
								</div>
							</div>
						</div>

						<div className="space-y-4">
							<Label className="text-sm font-medium text-gray-700">
								Are you currently working in a full-time role?
							</Label>
							<div className="flex items-center space-x-8">
								<div className="flex items-center space-x-3">
									<input
										type="radio"
										id="isCurrentlyEmployedYes"
										name="isCurrentlyEmployed"
										checked={formState.isCurrentlyEmployed === true}
										onChange={() =>
											setFormState((prev) => ({
												...prev,
												isCurrentlyEmployed: true,
											}))
										}
										className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<Label
										htmlFor="isCurrentlyEmployedYes"
										className="text-sm text-gray-700"
									>
										Yes
									</Label>
								</div>
								<div className="flex items-center space-x-3">
									<input
										type="radio"
										id="isCurrentlyEmployedNo"
										name="isCurrentlyEmployed"
										checked={formState.isCurrentlyEmployed === false}
										onChange={() =>
											setFormState((prev) => ({
												...prev,
												isCurrentlyEmployed: false,
											}))
										}
										className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<Label
										htmlFor="isCurrentlyEmployedNo"
										className="text-sm text-gray-700"
									>
										No
									</Label>
								</div>
							</div>
						</div>
					</div>

					{/* Conditional fields based on employment status */}
					{formState.isCurrentlyEmployed ? (
						<div className="space-y-2">
							<Label
								htmlFor="noticePeriod"
								className="text-sm font-medium text-gray-600"
							>
								Notice Period (days)
							</Label>
							<Input
								id="noticePeriod"
								type="text"
								placeholder="30"
								value={formState.noticePeriodInDays || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleIntegerInput(e.target.value, 'noticePeriodInDays')
								}
								className="max-w-xs"
							/>
						</div>
					) : (
						<div className="space-y-2">
							<Label
								htmlFor="monthsSinceLastRole"
								className="text-sm font-medium text-gray-600"
							>
								Months since last full-time role
							</Label>
							<Input
								id="monthsSinceLastRole"
								type="text"
								placeholder="3"
								value={formState.monthsSinceLastFullTimeRole || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleIntegerInput(
										e.target.value,
										'monthsSinceLastFullTimeRole'
									)
								}
								className="max-w-xs"
							/>
						</div>
					)}

					<div className="space-y-8">
						<div className="space-y-3">
							<Label
								htmlFor="technicalExperience"
								className="text-lg font-medium text-gray-700"
							>
								Years of full-time technical experience
							</Label>
							<p className="text-sm text-gray-500">
								Please enter the total number of years you have worked in
								technical roles.
							</p>
							<Input
								id="technicalExperience"
								type="text"
								placeholder="1.5"
								value={formState.yearsOfFullTimeTechnicalExperience || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleNumberInput(
										e.target.value,
										'yearsOfFullTimeTechnicalExperience'
									)
								}
							/>
						</div>
						<div className="space-y-3">
							<Label
								htmlFor="startupExperience"
								className="text-lg font-medium text-gray-700"
							>
								Years of full-time startup work experience
							</Label>
							<p className="text-sm text-gray-500">
								Please enter the total number of years you have worked at
								startup companies.
							</p>
							<Input
								id="startupExperience"
								type="text"
								placeholder="1.5"
								value={formState.yearsOfFullTimeStartupWorkExperience || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleNumberInput(
										e.target.value,
										'yearsOfFullTimeStartupWorkExperience'
									)
								}
							/>
						</div>
					</div>
				</div>

				{/* CTC Information */}
				<div className="space-y-6">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							Compensation Details
						</Label>
						<p className="text-sm text-gray-500">
							Please provide your current and expected compensation details.
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="space-y-3">
							<Label
								htmlFor="currentCTC"
								className="text-sm font-medium text-gray-700"
							>
								Current CTC (in LPA)
							</Label>
							<Input
								id="currentCTC"
								type="text"
								placeholder="28"
								value={formState.currentCTC || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleNumberInput(e.target.value, 'currentCTC')
								}
							/>
						</div>
						<div className="space-y-3">
							<Label
								htmlFor="expectedCTC"
								className="text-sm font-medium text-gray-700"
							>
								Expected CTC (in LPA)
							</Label>
							<Input
								id="expectedCTC"
								type="text"
								placeholder="32"
								value={formState.expectedCTC || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleNumberInput(e.target.value, 'expectedCTC')
								}
							/>
							<p className="text-xs text-gray-500 mt-1">
								We will only notify you of roles that meet or exceed your
								expected CTC.
							</p>
						</div>
					</div>
				</div>

				{/* Past Organizations */}
				<div className="space-y-4">
					<div>
						<Label className="text-lg font-medium text-gray-700 mb-2 block">
							What kind of organizations have you worked at in the past?
						</Label>
						<p className="text-sm text-gray-500">
							Please select all the types of organizations where you have worked
							previously.
						</p>
					</div>
					<MultiSelect
						options={questionOptions.pastOrganizationsKinds}
						selectedValues={formState.pastOrganizationsKinds}
						onSelectionChange={handlePastOrganizationKindChange}
						placeholder="Select past organization types..."
					/>
				</div>
			</div>

			<div className="flex justify-end pt-6">
				<Button
					onClick={handleSubmit}
					className="bg-blue-600 text-white hover:bg-blue-700 cursor-pointer px-8 py-2"
					disabled={!isFormValid() || isSubmitting}
				>
					{isSubmitting ? 'Submitting...' : 'Submit Profile'}
				</Button>
			</div>
		</div>
	);
};
